import { Eye, EyeOff } from "lucide-react";
import { useMemo, useState } from "react";
import { createAccountFormCopy } from "../../data/auth/createAccountPage";
import { authTheme } from "../../data/auth/authTheme";
import {
  evaluatePasswordRequirements,
  passwordMeetsRequirements,
} from "../../utils/auth/evaluatePasswordRequirements";
import { AuthFormDivider } from "./AuthFormDivider";
import { AuthSocialButtons } from "./AuthSocialButtons";
import { PasswordRequirementsPanel } from "./PasswordRequirementsPanel";
import {
  applyAuthInputBlur,
  applyAuthInputFocus,
  authInputClassName,
  authInputStyle,
} from "./authInputStyles";

export function CreateAccountFormPanel({
  firstName,
  lastName,
  email,
  password,
  error,
  isSubmitting,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onLogin,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const requirements = useMemo(
    () => evaluatePasswordRequirements(password),
    [password],
  );
  const canSubmit = passwordMeetsRequirements(requirements);

  return (
    <div className="font-inter flex min-h-screen flex-1 items-center justify-center bg-white px-6 py-10 sm:px-12 lg:px-20 xl:px-24">
      <div className="w-full max-w-[460px]">
        <p
          className="mb-8 text-center text-[28px] font-black tracking-[0.12em] lg:hidden"
          style={{ color: authTheme.panelBackground }}
        >
          ITEMA
        </p>

        <div className="mb-10">
          <h1
            className="text-[32px] font-bold leading-tight sm:text-[36px]"
            style={{ color: authTheme.heading }}
          >
            {createAccountFormCopy.title}
          </h1>
          <p className="mt-3 text-[15px]" style={{ color: authTheme.muted }}>
            {createAccountFormCopy.accountPrompt}{" "}
            <button
              type="button"
              onClick={onLogin}
              className="font-semibold hover:underline"
              style={{ color: authTheme.accent }}
            >
              {createAccountFormCopy.loginLink}
            </button>
          </p>
        </div>

        <form className="flex flex-col gap-6" onSubmit={onSubmit}>
          <div className="grid gap-6 sm:grid-cols-2">
            <label className="flex flex-col gap-2.5">
              <span
                className="text-sm font-semibold"
                style={{ color: authTheme.heading }}
              >
                {createAccountFormCopy.firstNameLabel}
              </span>
              <input
                type="text"
                name="firstName"
                autoComplete="given-name"
                required
                value={firstName}
                onChange={onFirstNameChange}
                className={authInputClassName}
                style={authInputStyle}
                onFocus={applyAuthInputFocus}
                onBlur={applyAuthInputBlur}
                placeholder={createAccountFormCopy.firstNamePlaceholder}
              />
            </label>

            <label className="flex flex-col gap-2.5">
              <span
                className="text-sm font-semibold"
                style={{ color: authTheme.heading }}
              >
                {createAccountFormCopy.lastNameLabel}
              </span>
              <input
                type="text"
                name="lastName"
                autoComplete="family-name"
                required
                value={lastName}
                onChange={onLastNameChange}
                className={authInputClassName}
                style={authInputStyle}
                onFocus={applyAuthInputFocus}
                onBlur={applyAuthInputBlur}
                placeholder={createAccountFormCopy.lastNamePlaceholder}
              />
            </label>
          </div>

          <label className="flex flex-col gap-2.5">
            <span
              className="text-sm font-semibold"
              style={{ color: authTheme.heading }}
            >
              {createAccountFormCopy.emailLabel}
            </span>
            <input
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={onEmailChange}
              className={authInputClassName}
              style={authInputStyle}
              onFocus={applyAuthInputFocus}
              onBlur={applyAuthInputBlur}
              placeholder={createAccountFormCopy.emailPlaceholder}
            />
          </label>

          <div className="flex flex-col gap-2.5">
            <label className="flex flex-col gap-2.5">
              <span
                className="text-sm font-semibold"
                style={{ color: authTheme.heading }}
              >
                {createAccountFormCopy.passwordLabel}
              </span>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={onPasswordChange}
                  className={`${authInputClassName} pr-12`}
                  style={authInputStyle}
                  onFocus={applyAuthInputFocus}
                  onBlur={applyAuthInputBlur}
                  placeholder={createAccountFormCopy.passwordPlaceholder}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-[#6B7280] hover:bg-[#F3F4F6]"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden />
                  )}
                </button>
              </div>
            </label>
            <PasswordRequirementsPanel requirements={requirements} />
          </div>

          {error ? (
            <p
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              role="alert"
            >
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={isSubmitting || !canSubmit}
            className="h-[52px] rounded-full text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:bg-primary-disabled disabled:shadow-none"
            style={{
              backgroundColor: authTheme.accent,
              boxShadow: authTheme.accentShadow,
            }}
            onMouseEnter={(event) => {
              if (!isSubmitting && canSubmit) {
                event.currentTarget.style.backgroundColor = authTheme.accentHover;
                event.currentTarget.style.boxShadow = authTheme.accentHoverShadow;
              }
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = authTheme.accent;
              event.currentTarget.style.boxShadow = authTheme.accentShadow;
            }}
            onMouseDown={(event) => {
              if (!isSubmitting && canSubmit) {
                event.currentTarget.style.backgroundColor = authTheme.accentActive;
                event.currentTarget.style.boxShadow = authTheme.accentActiveShadow;
              }
            }}
            onMouseUp={(event) => {
              if (!isSubmitting && canSubmit) {
                event.currentTarget.style.backgroundColor = authTheme.accentHover;
                event.currentTarget.style.boxShadow = authTheme.accentHoverShadow;
              }
            }}
          >
            {isSubmitting
              ? createAccountFormCopy.submittingLabel
              : createAccountFormCopy.submitLabel}
          </button>
        </form>

        <div className="mt-6">
          <AuthSocialButtons />
        </div>

        <AuthFormDivider label={createAccountFormCopy.orContinueWith} />

        <button
          type="button"
          onClick={onLogin}
          aria-label="Go to login page"
          className="h-[52px] w-full rounded-full border-2 bg-white text-[15px] font-semibold"
          style={{
            borderColor: authTheme.accent,
            color: authTheme.accent,
          }}
        >
          {createAccountFormCopy.loginButton}
        </button>

        <p
          className="mt-10 text-center text-xs leading-6"
          style={{ color: authTheme.muted }}
        >
          {createAccountFormCopy.footerPrefix}{" "}
          <a
            href="#"
            className="font-medium hover:underline"
            style={{ color: authTheme.accent }}
          >
            {createAccountFormCopy.termsLabel}
          </a>{" "}
          and{" "}
          <a
            href="#"
            className="font-medium hover:underline"
            style={{ color: authTheme.accent }}
          >
            {createAccountFormCopy.privacyLabel}
          </a>
          .
        </p>
      </div>
    </div>
  );
}
